import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import '../styles/Auth.css';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [ready, setReady]               = useState(false);

  // Supabase fires a RECOVERY event when the reset link is clicked.
  // We wait for that before showing the form.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setReady(true);
    });

    // If already in a session (user opened link in same browser tab)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    const pwd = password;
    // Clear from state immediately before the async call
    setPassword('');
    setConfirmPassword('');
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) throw error;
      navigate('/dashboard');
    } catch (e: any) {
      setError(e.message || 'Failed to update password. The reset link may have expired.');
      setLoading(false);
    }
  };

  if (!ready) return (
    <div className="auth-card-page">
      <div className="auth-card-panel">
        <p className="auth-card-subtitle" style={{ textAlign: 'center', margin: 0 }}>
          Verifying reset link…
        </p>
      </div>
    </div>
  );

  return (
    <div className="auth-card-page">
      <div className="auth-card-panel">
        <h2>Set new password</h2>
        <p className="auth-card-subtitle">Choose a strong password for your account.</p>
        {error && <div className="auth-card-alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-card-form">
          <div className="auth-card-field">
            <label className="auth-card-label" htmlFor="new-password">New Password</label>
            <div className="auth-card-input-wrap">
              <input
                id="new-password"
                className="auth-card-input has-affix"
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 6 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
                required autoFocus disabled={loading}
              />
              <button
                type="button"
                className="auth-card-affix-btn"
                onClick={() => setShowPassword(p => !p)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
              </button>
            </div>
          </div>

          <div className="auth-card-field">
            <label className="auth-card-label" htmlFor="confirm-password">Confirm Password</label>
            <input
              id="confirm-password"
              className="auth-card-input"
              type="password"
              placeholder="Repeat password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required disabled={loading}
            />
          </div>

          <button type="submit" className="auth-card-btn" disabled={loading}>
            {loading ? 'Saving…' : 'Save Password & Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
